import type { KeyChain } from './keyChains'
import { hasRecordedUse } from './lastUsed'

export type SortKey = 'created' | 'expires' | 'lastUsed'

export interface SortState {
  key: SortKey
  descending: boolean
}

/** A first click sorts oldest or soonest first; a second click on the same column reverses. */
export function nextSort(current: SortState | null, key: SortKey): SortState {
  return current?.key === key ? { key, descending: !current.descending } : { key, descending: false }
}

const time = (value: string) => new Date(value).getTime()

function recordedUse(chain: KeyChain, trackedSince: string): number | null {
  return chain.lastUsedAt && hasRecordedUse(chain.lastUsedAt, trackedSince) ? time(chain.lastUsedAt) : null
}

/** Keys with no recorded use first (stale keys on top), oldest created first, then oldest use. */
function compareLastUsed(a: KeyChain, b: KeyChain, trackedSince: string): number {
  const aUsed = recordedUse(a, trackedSince)
  const bUsed = recordedUse(b, trackedSince)
  if (aUsed === null || bUsed === null) {
    if (aUsed !== bUsed) return aUsed === null ? -1 : 1
    return time(a.createdAt) - time(b.createdAt)
  }
  return aUsed - bUsed
}

/** The table's rows. No sort, or Last used while tracking is off, keeps the given order. */
export function sortKeyChains(
  chains: KeyChain[],
  sort: SortState | null,
  trackedSince: string | null | undefined,
): KeyChain[] {
  if (!sort || (sort.key === 'lastUsed' && !trackedSince)) return chains
  const since = trackedSince ?? ''
  const compare = (a: KeyChain, b: KeyChain): number => {
    switch (sort.key) {
      case 'created':
        return time(a.createdAt) - time(b.createdAt)
      case 'expires':
        return time(a.current.expires_at) - time(b.current.expires_at)
      case 'lastUsed':
        return compareLastUsed(a, b, since)
    }
  }
  const sorted = [...chains].sort(compare)
  return sort.descending ? sorted.reverse() : sorted
}
